import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import {
  AssociationsSummaryService,
  BusinessImpactsService,
  RiskCaseOverviewService,
  AssociationsSummary,
  OpEvent,
  Issue,
  RiskCaseOverview,
  RiskCaseEnumItem,
} from 'oneorm-api-http-client';
import { delay, forkJoin, Observable, of, switchMap, map } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { BusinessImpactAdapter } from '../shared/adapters/business-impact.adapter';
import { OverlaySpinnerService } from '../shared/components/overlay-spinner/overlay-spinner.service';
import { TreeNode } from '../shared/components/selection-tree/tree.model';
import { StatusId } from '../shared/constants/constants';
import { RISK_CASE_TYPE_PATH_PARAM } from '../shared/constants/wizard.constants';
import { RiskCaseTypeParam, RiskCaseType } from '../shared/models/risk-case-type.model';
import { RiskCasesService } from '../shared/services/risk-cases.service';
import { ToastService } from '../shared/services/toast.service';
import { UserStore } from '../shared/store/user.store';
import { uniqBy } from '../shared/utils/array/uniq-by';
import { getEtagHeader } from '../shared/utils/object/get-etag-header';
import { isNil } from '../shared/utils/object/is-nil';
import { isEvent } from '../shared/utils/other/is-event';

export interface OverviewResolvedData {
  riskCaseType: RiskCaseType;
  riskCase: OpEvent | Issue;
  associations: AssociationsSummary;
  etag: string;
  businessImpactNodes: TreeNode<number>[];
  selectedBusinessUnitsNodes: TreeNode<number>[];
  businessImpact: RiskCaseEnumItem[];
  icofr?: RiskCaseOverview;
}

const isPreDraftStatus = (entity: { status?: { id?: number } }, riskCaseType: RiskCaseTypeParam): boolean =>
  riskCaseType === 'event' ? entity.status?.id === StatusId.EVENT_PRE_DRAFT : entity.status?.id === StatusId.ISSUE_PRE_DRAFT;

const filterUniqAssociations = (associations: AssociationsSummary): AssociationsSummary => ({
  ...associations,
  secondary_associations: {
    rcsas: uniqBy(associations.secondary_associations.rcsas, 'id'),
    risks: uniqBy(associations.secondary_associations.risks, 'id'),
    controls: uniqBy(associations.secondary_associations.controls, 'ctrl_id'),
    op_events: uniqBy(associations.secondary_associations.op_events, 'id'),
    legal_entities: uniqBy(associations.secondary_associations.legal_entities, 'id'),
  },
});

export const overviewResolver = (route: ActivatedRouteSnapshot): Observable<OverviewResolvedData | undefined> => {
  const riskCaseId = +route.params['id'];
  const riskCaseType = route.params[RISK_CASE_TYPE_PATH_PARAM] as RiskCaseTypeParam;

  if (isNil(riskCaseId)) {
    return of(undefined);
  }

  const overlaySpinnerService = inject(OverlaySpinnerService);
  const associationsSummaryService = inject(AssociationsSummaryService);
  const toastService = inject(ToastService);
  const riskCasesService = inject(RiskCasesService);
  const businessService = inject(BusinessImpactsService);
  const userStore = inject(UserStore);
  const router = inject(Router);

  overlaySpinnerService.showLoader();

  if (isEvent(riskCaseType)) {
    userStore.checkIfUserHaveUnitMemberRoleForRiskCase(riskCaseId);
    const riskCaseOverviewService = inject(RiskCaseOverviewService);

    return riskCasesService.getEvent(riskCaseId).pipe(
      switchMap(eventResponse => {
        const event = eventResponse.body;

        if (isPreDraftStatus(event, riskCaseType)) {
          overlaySpinnerService.hideLoader();
          router.navigate(['/']);
          return of(undefined);
        } else {
          return forkJoin([
            of(eventResponse),
            associationsSummaryService.getOpEventAssociationsSummary(riskCaseId),
            businessService.getBusinessImpacts('Event'),
            riskCaseOverviewService.getRiskCaseOverView(riskCaseId),
          ]).pipe(
            map(([riskCaseResponse, associations, businessUnits, riskCaseOverview]) => {
              const { business_impact } = riskCaseResponse.body;
              const businessImpactNodes = BusinessImpactAdapter.fromDto(businessUnits.data);
              const selectedBusinessUnitsNodes = BusinessImpactAdapter.findSelectedNodes(businessImpactNodes, business_impact);

              return {
                riskCaseType: 'Event' as RiskCaseType,
                riskCase: riskCaseResponse.body,
                associations: filterUniqAssociations(associations),
                etag: getEtagHeader(riskCaseResponse),
                businessImpactNodes,
                selectedBusinessUnitsNodes,
                businessImpact: business_impact,
                icofr: riskCaseOverview,
              };
            })
          );
        }
      }),
      catchError(error => {
        toastService.showErrorToast('Something went wrong.');
        console.error(error);
        overlaySpinnerService.hideLoader();
        throw error;
      }),
      delay(500),
      switchMap(data => {
        overlaySpinnerService.hideLoader();
        return of(data);
      })
    );
  }

  userStore.checkIfUserHaveUnitMemberRoleForRiskCase(riskCaseId);
  return riskCasesService.getIssue(riskCaseId).pipe(
    switchMap(issueResponse => {
      const issue = issueResponse.body;

      if (isPreDraftStatus(issue, riskCaseType)) {
        overlaySpinnerService.hideLoader();
        router.navigate(['/']);
        return of(undefined);
      } else {
        return forkJoin([
          of(issueResponse),
          associationsSummaryService.getIssueAssociationsSummary(riskCaseId),
          businessService.getBusinessImpacts('Issue'),
        ]).pipe(
          map(([response, associations, businessUnits]) => {
            const { business_impact } = response.body;
            const businessImpactNodes = BusinessImpactAdapter.fromDto(businessUnits.data);
            const selectedBusinessUnitsNodes = BusinessImpactAdapter.findSelectedNodes(businessImpactNodes, business_impact);

            return {
              riskCaseType: 'Issue' as RiskCaseType,
              riskCase: response.body,
              associations: filterUniqAssociations(associations),
              etag: getEtagHeader(response),
              businessImpactNodes,
              selectedBusinessUnitsNodes,
              businessImpact: business_impact,
            };
          })
        );
      }
    }),
    catchError(error => {
      toastService.showErrorToast('Something went wrong.');
      console.error(error);
      overlaySpinnerService.hideLoader();
      throw error;
    }),
    delay(500),
    switchMap(data => {
      overlaySpinnerService.hideLoader();
      return of(data);
    })
  );
};
